import { useEffect, useState } from 'react';

import { authService } from '../auth/AuthService';

import { flagManager, getFlagKey } from './FeatureFlagManager';
import { RemoteFeatureFlags } from './featureFlags';

/**
 * Hooks for remote feature flags. Flag values are evaluated once at session start
 * (after SDK init + identify) and do not change mid-session. The one exception is a slow
 * login: the anonymous value is served first and replaced once the user is identified.
 * Internal users get streaming updates for easier testing.
 * Available flags can be seen here:
 * https://app.launchdarkly.com/default/production/features?tag=RiksTV.Apps.WebApps
 */

interface FlagOptions<T = boolean> {
  defaultValue?: T;
}

const useStreamingUpdates = () => {
  // Enable streaming updates for internal users
  const [enableStreamingUpdates, setEnableStreamingUpdates] = useState(false);
  useEffect(() => {
    // Used to skip setState if the component unmounts before whenReady fires
    const abort = new AbortController();
    authService.whenReady(() => {
      if (!abort.signal.aborted) {
        setEnableStreamingUpdates(authService.isInternalUser());
      }
    });
    return () => abort.abort();
  }, []);
  return [enableStreamingUpdates];
};

export const useRemoteFeatureFlag = <T = boolean>(
  flagName: RemoteFeatureFlags,
  opts: FlagOptions<T> = {}
): [T | null] => {
  const { defaultValue } = opts;
  const flagKey = getFlagKey(flagName);
  const [enableStreamingUpdates] = useStreamingUpdates();

  const [value, setValue] = useState<T | null>(null);

  useEffect(() => {
    let cancelled = false;
    const evaluate = () =>
      flagManager.getValueWhenReady<T>(flagKey, defaultValue).then(newValue => {
        if (!cancelled) {
          setValue(newValue);
        }
      });
    evaluate();
    // init() gives up on auth after a few seconds, so a slow login is identified after the first evaluation
    flagManager.whenIdentified().then(evaluate);
    return () => {
      cancelled = true;
    };
  }, [defaultValue, flagKey]);

  useEffect(() => {
    if (enableStreamingUpdates) {
      return flagManager.notifyOnChanged(flagKey, setValue);
    }
  }, [enableStreamingUpdates, setValue, flagKey]);

  return [value];
};

// Just made it simpler to safely type the return value this way since default is optional in regular hook
export const useRemoteFeatureFlagWithDefault = <T = boolean>(flagName: RemoteFeatureFlags, defaultValue: T): T => {
  const [value] = useRemoteFeatureFlag<T>(flagName, { defaultValue });
  return value ?? defaultValue;
};
